import React, { useEffect, useRef } from 'react';

const EnergySphere = ({ size = 300 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    const baseRadius = size * 0.15;

    // Particle system for floating lines/particles
    const particles = [];
    const numParticles = 80;
    
    // Initialize particles
    for (let i = 0; i < numParticles; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = baseRadius + Math.random() * (size * 0.3);
      particles.push({
        x: centerX + Math.cos(angle) * distance,
        y: centerY + Math.sin(angle) * distance,
        originalX: centerX + Math.cos(angle) * distance,
        originalY: centerY + Math.sin(angle) * distance,
        angle: angle,
        distance: distance,
        speed: 0.002 + Math.random() * 0.003,
        size: 1 + Math.random() * 2,
        opacity: 0.3 + Math.random() * 0.4,
        phase: Math.random() * Math.PI * 2
      });
    }

    // Energy waves
    const waves = [];
    let lastWaveTime = 0;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = (currentTime - startTimeRef.current) / 1000;
      
      // Clear canvas
      ctx.clearRect(0, 0, size, size);
      
      // Calculate core glow cycle (3 seconds on, fade out)
      const cycleTime = 4; // 3 seconds glow + 1 second fade
      const cycleProgress = (elapsed % cycleTime) / cycleTime;
      let coreIntensity = 0;
      
      if (cycleProgress < 0.75) { // 3 seconds of 4 second cycle
        coreIntensity = 0.8 + 0.2 * Math.sin(elapsed * 4); // Gentle pulsing
      } else {
        // Fade out in last second
        const fadeProgress = (cycleProgress - 0.75) / 0.25;
        coreIntensity = (1 - fadeProgress) * 0.8;
      }

      // Create energy waves during core glow (only 2 waves per cycle)
      if (coreIntensity > 0.1 && currentTime - lastWaveTime > 1500 && waves.length < 2) {
        waves.push({
          radius: 0,
          opacity: 0.6,
          birthTime: currentTime
        });
        lastWaveTime = currentTime;
      }

      // Update and draw energy waves
      for (let i = waves.length - 1; i >= 0; i--) {
        const wave = waves[i];
        const waveAge = (currentTime - wave.birthTime) / 1000;
        wave.radius = waveAge * 80;
        wave.opacity = Math.max(0, 0.6 - waveAge * 0.3);

        if (wave.opacity > 0) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, wave.radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(249, 112, 104, ${wave.opacity * 0.5})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        } else {
          waves.splice(i, 1);
        }
      }

      // Draw floating particles/lines
      particles.forEach((particle, index) => {
        // Gentle floating motion
        particle.angle += particle.speed;
        particle.x = particle.originalX + Math.sin(elapsed + particle.phase) * 10;
        particle.y = particle.originalY + Math.cos(elapsed * 0.7 + particle.phase) * 8;
        
        // Rotate around center slightly
        const rotationAngle = elapsed * 0.1;
        const cos = Math.cos(rotationAngle);
        const sin = Math.sin(rotationAngle);
        const dx = particle.originalX - centerX;
        const dy = particle.originalY - centerY;
        particle.x = centerX + (dx * cos - dy * sin) + Math.sin(elapsed + particle.phase) * 10;
        particle.y = centerY + (dx * sin + dy * cos) + Math.cos(elapsed * 0.7 + particle.phase) * 8;

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(120, 120, 120, ${particle.opacity})`;
        ctx.fill();

        // Draw connecting lines to nearby particles
        particles.forEach((other, otherIndex) => {
          if (index < otherIndex) {
            const dist = Math.sqrt(
              (particle.x - other.x) ** 2 + (particle.y - other.y) ** 2
            );
            if (dist < 60) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = `rgba(140, 140, 140, ${0.1 * (1 - dist / 60)})`;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        });
      });

      // Draw glowing core
      if (coreIntensity > 0) {
        // Outer glow
        const gradient = ctx.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, baseRadius * 2
        );
        gradient.addColorStop(0, `rgba(249, 112, 104, ${coreIntensity * 0.8})`);
        gradient.addColorStop(0.5, `rgba(249, 112, 104, ${coreIntensity * 0.3})`);
        gradient.addColorStop(1, 'rgba(249, 112, 104, 0)');
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Inner core
        const coreGradient = ctx.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, baseRadius
        );
        coreGradient.addColorStop(0, `rgba(249, 132, 124, ${coreIntensity})`);
        coreGradient.addColorStop(0.7, `rgba(249, 112, 104, ${coreIntensity * 0.8})`);
        coreGradient.addColorStop(1, `rgba(249, 112, 104, ${coreIntensity * 0.2})`);
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
        ctx.fillStyle = coreGradient;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="block"
      style={{
        background: 'transparent'
      }}
    />
  );
};

export default EnergySphere;